import {action, observable} from "mobx";
import i18n from "../i18n";
import userService from "../services/userService";
import generalReportService from "services/generalReportService";
import appStore from "./appStore";
import {b64DecodeToUrl} from "../utils/helpers";

class userStore {
    @observable currentCredit = 0;

    @action getUserList = async params => {
        const resultOfRequest = await userService.getUserList(params);
        if (resultOfRequest.status !== 200) {
            appStore.openErrorModal({content: resultOfRequest.data.message});
            return resultOfRequest;
        }
        return resultOfRequest.data;
    };

    @action createUser = async params => {
        const resultOfRequest = await userService.createUser(params);
        if (resultOfRequest.status !== 200) {
            if (
                resultOfRequest?.data?.result?.validationFailed &&
                !["bm", "sy", "agency"].includes(params?.role)
            ) {
                return resultOfRequest?.data;
            }
            appStore.openErrorModal({content: resultOfRequest.data.message});
            return false;
        }
        appStore.showSuccessMessage({
            content: i18n.t("stores.user.createUser.successMessage", {ns: "common"})
        });
        appStore.getCompanyUserList();
        return resultOfRequest.data;
    };

    @action getUser = async params => {
        const resultOfRequest = await userService.getUser(params);
        return resultOfRequest;
    };

    @action getUserReport = async params => {
        await appStore.setLoading(true);
        const resultOfRequest = await generalReportService.getUserReport(params);
        await appStore.setLoading(false);
        if (resultOfRequest.status !== 200) {
            appStore.openErrorModal({content: resultOfRequest.data.message});
            return [];
        }
        return b64DecodeToUrl(resultOfRequest.data.result);
    };

    @action getUserCreditStats = async params => {
        const resultOfRequest = await userService.getUserCreditStats(params);
        if (resultOfRequest.status === 200) {
            this.currentCredit = resultOfRequest.data?.data?.stats?.currentCredit || 0;
        }
        return resultOfRequest;
    };

    @action updateUser = async params => {
        const resultOfRequest = await userService.updateUser(params);
        if (resultOfRequest.status !== 200) {
            if (
                resultOfRequest?.data?.result?.validationFailed &&
                !["bm", "sy", "agency"].includes(params?.user?.role)
            ) {
                return resultOfRequest?.data;
            }
            appStore.openErrorModal({content: resultOfRequest.data.message});
            return false;
        }
        appStore.showSuccessMessage({
            content: i18n.t("stores.user.updateUser.successMessage", {ns: "common"})
        });
        appStore.getCompanyUserList();
        return true;
    };

    @action getUserInfo = async params => {
        const resultOfRequest = await userService.getUserInfo(params);
        return resultOfRequest.data;
    };

    @action getAccessibleFields = async params => {
        const resultOfRequest = await userService.getAccessibleFields(params);
        return resultOfRequest;
    };

    @action updateCommunicationPreferences = async params => {
        const resultOfRequest = await userService.updateCommunicationPreferences(params);
        if (resultOfRequest.status !== 200) {
            appStore.openErrorModal({content: resultOfRequest.data.message});
            return false;
        }
        return resultOfRequest;
    };
    @action getCommunicationPreferences = async params => {
        const resultOfRequest = await userService.getCommunicationPreferences(params);
        if (resultOfRequest.status !== 200) {
            appStore.openErrorModal({content: resultOfRequest.data.message});
            return false;
        }
        return resultOfRequest;
    };
    @action changeUserPassword = async params => {
        const resultOfRequest = await userService.changeUserPassword(params);
        if (resultOfRequest.status !== 200) {
            appStore.openErrorModal({content: resultOfRequest.data.message});
            return false;
        }
        appStore.showSuccessMessage({
            content: i18n.t("stores.courier.changePassword.successMessageContent", {
                ns: "common"
            })
        });
        return true;
    };
    @action getUserPermissionPolicies = async params => {
        const resultOfRequest = await userService.getUserPermissionPolicies(params);
        if (resultOfRequest.status !== 200) {
            appStore.openErrorModal({content: resultOfRequest.data.message});
            return false;
        }
        return resultOfRequest.data.success ? resultOfRequest.data.permissionPolicies : [];
    };
    @action getAssignedUserGroups = async params => {
        const resultOfRequest = await userService.getAssignedUserGroups(params);
        if (resultOfRequest.status !== 200) {
            appStore.openErrorModal({content: resultOfRequest.data.message});
            return false;
        }
        return resultOfRequest.data;
    };

    @action getAuthorizedPoolCities = async () => {
        const resultOfRequest = await userService.getAuthorizedPoolCities();
        if (resultOfRequest.status !== 200) {
            appStore.openErrorModal({content: resultOfRequest.data.message});
            return false;
        }
        return resultOfRequest.data;
    };

    @action assignUserGroup = async params => {
        const resultOfRequest = await userService.assignUserGroup(params);
        if (resultOfRequest.status !== 200) {
            appStore.openErrorModal({content: resultOfRequest.data.message});
            return false;
        }
        return resultOfRequest.data;
    };
    @action unassignUserGroup = async params => {
        const resultOfRequest = await userService.unassignUserGroup(params);
        if (resultOfRequest.status !== 200) {
            appStore.openErrorModal({content: resultOfRequest.data.message});
            return false;
        }
        return resultOfRequest.data;
    };
    @action getAllowedPermissionPolicies = async params => {
        const resultOfRequest = await userService.getAllowedPermissionPolicies(params);
        if (resultOfRequest.status !== 200) {
            appStore.openErrorModal({content: resultOfRequest.data.message});
            return false;
        }
        return resultOfRequest.data;
    };

    @action assignAllowedPermissionPolicies = async params => {
        const resultOfRequest = await userService.assignAllowedPermissionPolicies(params);
        if (resultOfRequest.status !== 200) {
            appStore.openErrorModal({content: resultOfRequest.data.message});
            return false;
        }
        return resultOfRequest.data;
    };

    @action unassignAllowedPermissionPolicy = async params => {
        const resultOfRequest = await userService.unassignAllowedPermissionPolicy(params);
        if (resultOfRequest.status !== 200) {
            appStore.openErrorModal({content: resultOfRequest.data.message});
            return false;
        }
        return resultOfRequest.data;
    };

    @action getDisallowedPermissionPolicies = async params => {
        const resultOfRequest = await userService.getDisallowedPermissionPolicies(params);
        if (resultOfRequest.status !== 200) {
            appStore.openErrorModal({content: resultOfRequest.data.message});
            return false;
        }
        return resultOfRequest.data;
    };

    @action assignDisallowedPermissionPolicies = async params => {
        const resultOfRequest = await userService.assignDisallowedPermissionPolicies(params);
        if (resultOfRequest.status !== 200) {
            appStore.openErrorModal({content: resultOfRequest.data.message});
            return false;
        }
        return resultOfRequest.data;
    };

    @action unassignDisallowedPermissionPolicy = async params => {
        const resultOfRequest = await userService.unassignDisallowedPermissionPolicy(params);
        if (resultOfRequest.status !== 200) {
            appStore.openErrorModal({content: resultOfRequest.data.message});
            return false;
        }
        return resultOfRequest.data;
    };

    @action bulkUpdateUsers = async params => {
        try {
            const resultOfRequest = await userService.bulkUpdateUsers(params);
            if (resultOfRequest.status !== 200) {
                appStore.openErrorModal({content: resultOfRequest.data.message});
                return false;
            }
            return resultOfRequest.data;
        } catch (error) {
            appStore.openErrorModal({
                content: error.message || "Toplu güncelleme sırasında bir hata oluştu"
            });
            return false;
        }
    };
}

export default new userStore();
